import i18n from '@/lang/i18n'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import { store } from '@/store/store'
import AttachmentCountRenderer from '@/cellRenderers/common/AttachmentCountRenderer.vue'
import { AgGridColumnType, AgGridExportType } from '@/shared/models/ag-grid.model'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'

export const registryRequestGridColumnDefs =
    (
        onIdClicked: (params: any) => void,
        onIncidentClicked: (params: any) => void
    ) =>
        ([
            { type: AgGridColumnType.CUSTOM_CHECKBOX },
            {
                type: AgGridColumnType.MASTER_DETAIL,
                colId: 'expand.registry',
                showRowGroup: true
            },
            {
                headerName: i18n.tc('global.request'),
                colId: 'request',
                children: [
                    {
                        headerName: i18n.tc('grid.headerName.requestLabel'),
                        field: 'label',
                        type: AgGridColumnType.TEXT,
                        onCellClicked: onIdClicked,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 110
                    },
                    {
                        headerName: i18n.tc('global.status'),
                        field: 'status',
                        type: AgGridColumnType.COMMON_REQUEST_STATUS,
                        exportField: 'status.name',
                        exportType: AgGridExportType.TEXT,
                        suppressMenu: false
                    },
                    {
                        headerName: i18n.tc('grid.reqRequestDate'),
                        field: 'reqRequestDate',
                        type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                        exportType: AgGridExportType.DATETIME
                    },
                    {
                        headerName: i18n.tc('global.priority'),
                        colId: 'priority.level',
                        field: 'priority',
                        type: AgGridColumnType.PRIORITY,
                        exportField: 'priority.name',
                        exportType: AgGridExportType.TEXT
                    },
                    {
                        headerName: i18n.tc('global.episode'),
                        field: 'reqEpisode',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        width: 110,
                        suppressSizeToFit: true
                    },
                    {
                        headerName: i18n.tc('registry.cases.professional'),
                        colId: 'physicianPetitioner.normalizedFullName',
                        field: 'physicianPetitioner.fullName',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('registry.cases.unit'),
                        field: 'physicianDepartment.name',
                        type: AgGridColumnType.DEPARTMENTS,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('global.center'),
                        field: 'physicianDepartment.center.name',
                        type: AgGridColumnType.CENTERS,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('global.externalReference'),
                        field: 'externalReference',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('global.automaticCreation'),
                        headerTooltip: i18n.tc('global.automaticCreation'),
                        headerComponentParams: {
                            template: buildIconHeaderTemplate('far fa-magic')
                        },
                        field: 'automaticallyCreated',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.BOOLEAN,
                        cellRenderer: ({ value }: ICellRendererParams) => value ? '<i class="success far fa-check"/>' : '',
                        filter: false,
                        hide: false,
                        minWidth: 80
                    },
                    {
                        headerName: i18n.tc('global.insurance'),
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        field: 'insurer.name',
                        filter: 'agTextColumnFilter'
                    },
                    {
                        headerName: i18n.tc('global.insuranceDocument'),
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        field: 'referralNumber',
                        filter: 'agTextColumnFilter'
                    }
                ]
            },
            {
                headerName: i18n.tc('global.case'),
                children: [
                    {
                        headerName: i18n.tc('grid.casCasenumber'),
                        field: 'caseStudy.casCasenumber',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 110
                    },
                    {
                        headerName: i18n.tc('grid.casRegistrationDate'),
                        field: 'caseStudy.casRegistrationDate',
                        type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                        exportType: AgGridExportType.DATETIME
                    },
                    {
                        headerName: i18n.tc('grid.casValidationDate'),
                        field: 'caseStudy.casValidationDate',
                        type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                        exportType: AgGridExportType.DATETIME
                    },
                    {
                        headerName: i18n.tc('grid.labName'),
                        field: 'laboratory.labName',
                        type: AgGridColumnType.LABORATORIES,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    }
                ]
            },
            {
                headerName: i18n.tc('global.patient'),
                colId: 'patient',
                children: [
                    {
                        headerValueGetter: () => store.getters.parameters.global?.mainPatientIdentifierType.name,
                        colId: 'patient.personIdentifier',
                        field: 'patient.mainPatientIdentifier.paiIdentifier',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        sortable: false,
                        filterParams: {
                            filterOptions: ['equals'],
                            defaultOption: 'equals',
                            newRowsAction: 'keep'
                        },
                        minWidth: 110
                    },
                    {
                        headerValueGetter: () => store.getters.parameters.global?.secondaryPatientIdentifierType.name,
                        colId: 'patient.centerIdentifier',
                        field: 'patient.secondaryPatientIdentifier.paiIdentifier',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        sortable: false,
                        filterParams: {
                            filterOptions: ['equals'],
                            defaultOption: 'equals',
                            newRowsAction: 'keep'
                        },
                        minWidth: 110
                    },
                    {
                        headerName: i18n.tc('grid.patName'),
                        colId: 'patient.patName',
                        field: 'patient.name',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('grid.patMiddleName'),
                        colId: 'patient.patMiddleName',
                        field: 'patient.middleName',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('grid.patSurname1'),
                        colId: 'patient.patSurname1',
                        field: 'patient.surname1',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    },
                    {
                        headerName: i18n.tc('grid.patSurname2'),
                        colId: 'patient.patSurname2',
                        field: 'patient.surname2',
                        type: AgGridColumnType.TEXT,
                        exportType: AgGridExportType.TEXT,
                        minWidth: 170
                    }
                ]
            }, {
                headerName: i18n.tc('grid.hasAlarms'),
                headerTooltip: i18n.tc('grid.hasAlarms'),
                headerComponentParams: {
                    template: buildIconHeaderTemplate('far fa-exclamation-triangle')
                },
                field: 'incidents',
                type: AgGridColumnType.INCIDENT,
                exportType: AgGridExportType.INTEGER,
                onCellClicked: onIncidentClicked,
                hide: true,
                minWidth: 80
            },
            {
                headerName: i18n.tc('grid.attachments'),
                headerTooltip: i18n.tc('grid.attachments'),
                headerComponentParams: {
                    template: buildIconHeaderTemplate('far fa-paperclip')
                },
                field: 'attachments',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.INTEGER,
                cellRendererFramework: AttachmentCountRenderer,
                filter: false,
                hide: false,
                minWidth: 80
            }
        ])
